<template>
    <section class="wallet-level-up-modal">
        <div v-if="!isTradingType"
            class="title"
        >
            {{ $t(`kycLevelUpModal.${currencyShortCode}.title`) }}
        </div>

        <img class="image"
            :src="backroundImage"
        >

        <div>
            <div v-if="isOnFirstLevel"
                class="text"
            >
                {{ $t(`kycLevelUpModal.${currencyShortCode}.subtitleLongLevel1`) }}
            </div>
            <div v-else-if="isOnMaxLevel"
                class="text"
            >
                {{ $t(`kycLevelUpModal.${currencyShortCode}.subtitleLongMax`) }}
            </div>
            <div v-else
                class="text"
            >
                {{ $t(`kycLevelUpModal.${currencyShortCode}.subtitleLongLevel${kycLevel+1}`) }}
            </div>
        </div>

        <div class="table">
            <div class="table__header">{{ $t(`kycLevelUpModal.${currencyShortCode}.tableTitle`) }}</div>
            <div class="table__row table__row--bordered">
                <div class="table__column table__column--verification">{{ $t(`profile.kyc.level${kycLevel}VerifiedText`) }}</div>
                <div v-if="!isOnMaxLevel"
                    class="table__column table__column--next-level table__column--verification"
                >
                    {{ $t(`profile.kyc.level${kycLevel + 1}VerifiedText`) }}
                </div>
            </div>

            <div class="table__row">
                <div class="table__column table__column--align-left">
                    {{ $t(`kycLevelUpModal.${currencyShortCode}.tableTopup`) }}
                </div>
                <div class="table__column">{{ getFormattedNumber(currentLevel.topUpAndReceiveLimit) }}</div>
                <div v-if="!isOnMaxLevel"
                    class="table__column table__column--next-level"
                >
                    {{ getFormattedNumber(nextLevel.topUpAndReceiveLimit) }}
                </div>
            </div>
            <div v-if="isInternationalRegulation"
                class="table__row table__row--light"
            >
                <div class="table__column table__column--align-left">{{ currentLevel.topUpAndReceiveLimitFiatCurrency }}</div>
                <div class="table__column">{{ getFormattedNumber(currentLevel.topUpAndReceiveLimitFiat) }}</div>
                <div v-if="!isOnMaxLevel"
                    class="table__column table__column--next-level"
                >
                    {{ getFormattedNumber(nextLevel.topUpAndReceiveLimitFiat) }}
                </div>
            </div>
            <div v-if="isOnZeroLevel || isOnFirstLevel"
                class="table__row table__row--bordered-top"
            >
                <div class="table__column table__column--align-left">
                    {{ $t(`kycLevelUpModal.chz.tableSellTokens`) }}
                </div>
                <div class="table__column">{{ 0 }}</div>
                <div class="table__column table__column--next-level">
                    {{ isOnFirstLevel ? '∞' : 0 }}
                </div>
            </div>
        </div>

        <div class="buttons">
            <div class="button button--success"
                @click="goToKyc"
            >
                {{ $t(isOnMaxLevel ? 'global.button.contactSupport' : 'kycLevelUpModal.submitBtn') }}
            </div>
            <div class="button button--cancel"
                @click="cancelAction"
            >
                {{ $t('global.button.cancel') }}
            </div>
        </div>
    </section>
</template>

<script>
// 3rd party
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Utils
import {
    formatNumber,
} from '@shared/utils/formatter.js';

// Mixins
import kycMixin from '@webapp/helpers/kyc-mixin.js';

// Local Types
import localEpicTypes from '@webapp/store/types.js';

// Shared Modules
import {
    EventNames,
} from '@shared/utils/analytics-keys';

const maxLevel = 4;

export default {
    name: 'wallet-level-up-modal',
    mixins: [kycMixin],
    props: {
        kycLevel: {
            type: Number,
            default: 0,
        },
        type: {
            type: String,
            default: 'topup',
        },
    },
    computed: {
        ...mapGetters([
            'defaultCurrency',
            'modal',
            'isInternationalRegulation',
            'isPartnerApp',
            'partnerAppData',
            'userId',
        ]),

        isOnZeroLevel() {
            return this.kycLevel === 0;
        },

        isOnFirstLevel() {
            return this.kycLevel === 1;
        },

        isOnMaxLevel() {
            return this.kycLevel >= maxLevel;
        },

        isTradingType() {
            return this.type === 'trading';
        },

        backroundImage() {
            return require(`@webapp/assets/level-up-modal/${this.currencyShortCode}-level-${this.kycLevel}.webp`);
        },

        maxLevel() {
            return this.kycLevel === 4;
        },

        currencyShortCode() {
            return this.defaultCurrency.shortCode.toLowerCase();
        },
    },
    created() {
        this.kycMixinSetLevel(this.kycLevel);
        this.triggerAnalytics(this.kycLevel);
    },
    methods: {
        ...mapActions({
            resetModal: localEpicTypes.RESET_MODAL,
        }),
        getFormattedNumber(number) {
            return formatNumber(number);
        },
        displayCurrency(level) {
            const currency = level.topUpAndReceiveLimitFiatCurrency || '';

            return currency ? formatNumber(level.topUpAndReceiveLimitFiat) : '';
        },
        cancelAction() {
            this.resetModal();
        },
        goToKyc() {
            this.resetModal();
            if (this.isOnMaxLevel) {
                this.$router.push('/contact-us');
            } else {
                this.$router.push('/kyc');
            }
        },
        triggerAnalytics(kycLevel) {
            if (this.isPartnerApp) {
                this.$webTrackingInstance?.sendEvent(
                    EventNames.PartnerCrossSellPopup,
                    [
                        {
                            key: 'partner',
                            value: this.partnerAppData.id,
                        },
                        {
                            key: 'user',
                            value: this.userId,
                        },
                        {
                            key: 'modalType',
                            value: 'deposit',
                        },
                        {
                            key: 'triggeredFrom',
                            value: 'level-' + kycLevel,
                        },
                    ]
                );
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.title {
    text-align: center;
    font-size: 2.5rem;
    font-weight: 700;
    line-height: 2.93rem;
    white-space: pre-line;
    color: $cyprus-color;

    @include max-width(sm) {
        font-size: 2rem;
    }

    &--partner {
        padding-top: 2rem;
    }
}

.image {
    display: block;
    margin: 0 auto;
    width: 70%;
    min-height: 13rem;

    @include min-width(md) {
        min-height: 22rem;
    }

    @include min-width(lg) {
        min-height: 33rem;
    }
}

.text {
    padding: 2rem 0 4rem;
    font-size: 1.6rem;
    text-align: center;

    &--partner {
        padding: 1rem 0 0;

        &:last-child {
            padding-bottom: 1rem;
        }
    }
}

.table {
    padding: 2rem;
    border: 0.1rem solid $nobel-color;
    border-radius: 1rem;
    display: flex;
    flex-wrap: wrap;

    &__header {
        font-size: 2rem;
        line-height: 2.7rem;
        font-weight: 700;
        color: $cyprus-color;
        background: $white-color;
        padding: 0 2rem;
        margin-top: -3.5rem;

        @include max-width(sm) {
            font-size: 1.6rem;
        }
    }

    &__row {
        width: 100%;
        display: flex;
        justify-content: flex-end;
        font-weight: bold;

        &--bordered {
            border-bottom: 0.1rem solid $nobel-color;
        }

        &--bordered-top {
            border-top: 0.1rem solid $nobel-color;
        }

        &--light {
            font-weight: normal;
        }
    }

    &__column {
        padding: 1rem 2rem;
        min-width: 5rem;
        margin-left: 3rem;
        font-size: 1.5rem;
        color: $cyprus-color;
        display: flex;
        justify-content: flex-end;
        align-items: center;

        @include min-width(md) {
            min-width: 10rem;
        }

        @include max-width(sm) {
            margin-left: 0;
            font-size: 1.2rem;
            padding: 1rem;
        }

        &--next-level {
            background: rgba($malachite-color, 0.05);
        }

        &--verification {
            width: min-content;
            text-align: right;
        }

        &--align-left {
            margin-left: 0;
            margin-right: auto;
            text-align: left;
            justify-content: flex-start;
        }
    }
}

.buttons {
    display: flex;
    align-items: center;
    flex-direction: column;
    margin: 2rem 0;
    width: 100%;
}

.button {
    text-align: center;
    width: 34rem;
    margin: 2rem 0 0;

    @include max-width(sm) {
        width: 25rem;
    }

    &--success {
        line-height: 4rem;
        border-radius: 0.4rem;
        color: $daintree-color;
        background: $lima-color;
    }

    &--cancel {
        background: none;
        color: $cyprus-color;
    }
}
</style>